import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiCalendar, FiVideo } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import CrmModalShell from './CrmModalShell';
import { useAuth } from '../context/AuthContext';

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

/**
 * Consultant: create appointment from calendar slot (writes to Google Calendar with Meet).
 */
export default function CrmCalendarCreateModal({ open, onClose, initialSlot = null }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [startsLocal, setStartsLocal] = useState('');
  const [endsLocal, setEndsLocal] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const { data: candidates = [] } = useQuery(
    ['crm-create-candidates', user?.role],
    () =>
      user?.role === 'admin'
        ? api.get('/candidates').then((r) => r.data)
        : api.get('/candidates/assigned').then((r) => r.data),
    { enabled: open && Boolean(user), staleTime: 60_000 }
  );

  useEffect(() => {
    if (!open) return;
    setError('');
    setTitle('');
    setNotes('');
    setCandidateId('');
    if (initialSlot?.starts_at) {
      setStartsLocal(toLocalInputValue(initialSlot.starts_at));
      setEndsLocal(toLocalInputValue(initialSlot.ends_at || initialSlot.starts_at));
    } else {
      const start = new Date();
      start.setMinutes(0, 0, 0);
      start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + 30 * 60_000);
      setStartsLocal(toLocalInputValue(start.toISOString()));
      setEndsLocal(toLocalInputValue(end.toISOString()));
    }
  }, [open, initialSlot]);

  const createMutation = useMutation(
    () =>
      api.post('/crm/calendar/events', {
        title: title.trim() || undefined,
        candidate_id: candidateId ? Number(candidateId) : undefined,
        starts_at: fromLocalInputValue(startsLocal),
        ends_at: fromLocalInputValue(endsLocal),
        notes: notes || undefined,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['crm-calendar']);
        queryClient.invalidateQueries(['crm']);
        onClose?.();
      },
      onError: (e) => setError(e.response?.data?.error || e.message),
    }
  );

  return (
    <CrmModalShell
      open={open}
      onClose={onClose}
      size="sm"
      className="crm-modal--appointment"
      icon={FiCalendar}
      title="New appointment"
      subtitle="Adds a Google Calendar event with Meet for the selected candidate."
      ariaLabel="Create appointment"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <LoadingButton
            type="button"
            className="btn btn-primary"
            loading={createMutation.isLoading}
            disabled={!startsLocal || !endsLocal || !candidateId}
            onClick={() => {
              setError('');
              createMutation.mutate();
            }}
          >
            <FiVideo /> Create with Meet
          </LoadingButton>
        </>
      }
    >
      {error && (
        <p className="crm-modal-alert" role="alert">
          {error}
        </p>
      )}
      <div className="crm-modal-badge">
        <FiVideo aria-hidden /> Google Meet included
      </div>
      <label className="form-field">
        <span>Title</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Career coaching call"
          autoComplete="off"
        />
      </label>
      <label className="form-field">
        <span>Candidate</span>
        <select value={candidateId} onChange={(e) => setCandidateId(e.target.value)}>
          <option value="">Select candidate…</option>
          {(Array.isArray(candidates) ? candidates : []).map((c) => (
            <option key={c.id} value={c.id}>
              {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}
            </option>
          ))}
        </select>
      </label>
      <div className="crm-modal-row crm-modal-row--times">
        <label className="form-field">
          <span>Starts</span>
          <input
            type="datetime-local"
            value={startsLocal}
            onChange={(e) => setStartsLocal(e.target.value)}
          />
        </label>
        <label className="form-field">
          <span>Ends</span>
          <input
            type="datetime-local"
            value={endsLocal}
            onChange={(e) => setEndsLocal(e.target.value)}
          />
        </label>
      </div>
      <label className="form-field">
        <span>Notes</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional agenda or context"
        />
      </label>
    </CrmModalShell>
  );
}
